import React, { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import BaseModal from "../modals/BaseModal";
import Button from "../forms/Button";
import Input from "../forms/Input";
import { projectService, squadService, sprintService, apiErrorMessage } from "../../api/taskBoardService";
import { isSafeUrl, toDateInput } from "./shared";

/**
 * Create/edit forms for projects, squads and sprints. Each validates on the
 * client for fast feedback; the API validates again and is authoritative.
 */

const fieldClass = "input-field w-full";

const TextArea = ({ label, value, onChange, rows = 3, maxLength, hint }) => (
  <label className="flex flex-col gap-1.5">
    <span className="text-sm font-medium text-gray-700 dark:text-light-200">{label}</span>
    <textarea value={value} onChange={onChange} rows={rows} maxLength={maxLength} className={`${fieldClass} resize-y`} />
    {hint && <span className="text-xs text-gray-500 dark:text-light-500">{hint}</span>}
  </label>
);

const ErrorBanner = ({ message }) =>
  message ? (
    <div role="alert" className="text-sm p-3 rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400">
      {message}
    </div>
  ) : null;

/** Shared submit/close plumbing for the catalog forms. */
const useCatalogForm = ({ isOpen, initial, validate, save, onSaved }) => {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setForm(initial);
      setErrors({});
      setServerError("");
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (key) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const submit = async (e) => {
    e?.preventDefault();
    const er = validate(form);
    setErrors(er);
    if (Object.keys(er).length) return;
    setSaving(true);
    setServerError("");
    try {
      const res = await save(form);
      onSaved(res.data);
    } catch (err) {
      setServerError(apiErrorMessage(err, "Could not save"));
    } finally {
      setSaving(false);
    }
  };

  return { form, setForm, set, errors, serverError, saving, submit };
};

const Footer = ({ onClose, onSubmit, saving, label }) => (
  <div className="flex w-full justify-end gap-2">
    <Button variant="secondary" onClick={onClose} disabled={saving}>
      Cancel
    </Button>
    <Button onClick={onSubmit} disabled={saving}>
      {saving ? "Saving…" : label}
    </Button>
  </div>
);

// ---------------------------------------------------------------- Project

export const ProjectFormModal = ({ isOpen, onClose, project, onSaved }) => {
  const isEdit = Boolean(project);
  const { form, set, errors, serverError, saving, submit } = useCatalogForm({
    isOpen,
    initial: {
      name: project?.name || "",
      description: project?.description || "",
      color: project?.color || "#e68005",
      developmentUrl: project?.developmentUrl || "",
      productionUrl: project?.productionUrl || "",
    },
    validate: (f) => {
      const er = {};
      if (!f.name.trim()) er.name = "Name is required";
      if (!/^#[0-9a-fA-F]{6}$/.test(f.color)) er.color = "Use a hex color like #e68005";
      if (!isSafeUrl(f.developmentUrl)) er.developmentUrl = "Enter a valid http(s) URL";
      if (!isSafeUrl(f.productionUrl)) er.productionUrl = "Enter a valid http(s) URL";
      return er;
    },
    save: (f) => {
      const body = { ...f, name: f.name.trim(), developmentUrl: f.developmentUrl.trim(), productionUrl: f.productionUrl.trim() };
      return isEdit ? projectService.update(project._id, body) : projectService.create(body);
    },
    onSaved,
  });

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? "Edit project" : "New project"}
      footer={<Footer onClose={onClose} onSubmit={submit} saving={saving} label={isEdit ? "Save" : "Create project"} />}
    >
      <form onSubmit={submit} className="space-y-4" noValidate>
        <ErrorBanner message={serverError} />
        <Input label="Name" required value={form.name} onChange={set("name")} error={errors.name} maxLength={100} autoFocus />
        <TextArea label="Description" value={form.description} onChange={set("description")} maxLength={2000} />
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-gray-700 dark:text-light-200">Label color</span>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={/^#[0-9a-fA-F]{6}$/.test(form.color) ? form.color : "#e68005"}
              onChange={set("color")}
              className="h-10 w-14 rounded-lg border border-gray-300 dark:border-dark-100 bg-transparent cursor-pointer"
              aria-label="Pick label color"
            />
            <input value={form.color} onChange={set("color")} className={`${fieldClass} max-w-[140px] font-mono`} aria-label="Hex color" />
          </div>
          {errors.color && <span className="text-sm text-red-500">{errors.color}</span>}
        </div>
        <Input label="Development URL" type="url" placeholder="https://dev.example.com" value={form.developmentUrl} onChange={set("developmentUrl")} error={errors.developmentUrl} />
        <Input label="Production URL" type="url" placeholder="https://example.com" value={form.productionUrl} onChange={set("productionUrl")} error={errors.productionUrl} />
        <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
      </form>
    </BaseModal>
  );
};

// ---------------------------------------------------------------- Squad

export const SquadFormModal = ({ isOpen, onClose, squad, onSaved }) => {
  const isEdit = Boolean(squad);
  const { form, set, errors, serverError, saving, submit } = useCatalogForm({
    isOpen,
    initial: { name: squad?.name || "", description: squad?.description || "" },
    validate: (f) => (f.name.trim() ? {} : { name: "Name is required" }),
    save: (f) => {
      const body = { ...f, name: f.name.trim() };
      return isEdit ? squadService.update(squad._id, body) : squadService.create(body);
    },
    onSaved,
  });

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? "Edit squad" : "New squad"}
      footer={<Footer onClose={onClose} onSubmit={submit} saving={saving} label={isEdit ? "Save" : "Create squad"} />}
    >
      <form onSubmit={submit} className="space-y-4" noValidate>
        <ErrorBanner message={serverError} />
        <Input label="Name" required value={form.name} onChange={set("name")} error={errors.name} maxLength={100} autoFocus />
        <TextArea label="Description" value={form.description} onChange={set("description")} maxLength={2000} />
        <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
      </form>
    </BaseModal>
  );
};

// ---------------------------------------------------------------- Sprint

const MilestonesEditor = ({ value, onChange }) => {
  const update = (i, patch) => onChange(value.map((m, idx) => (idx === i ? { ...m, ...patch } : m)));
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700 dark:text-light-200">Timeline & milestones</span>
        <Button variant="ghost" size="sm" onClick={() => onChange([...value, { title: "", date: "", description: "", done: false }])}>
          <Plus className="w-4 h-4" /> Add milestone
        </Button>
      </div>
      {value.length === 0 && <p className="text-xs text-gray-500 dark:text-light-500">No milestones yet.</p>}
      {value.map((m, i) => (
        <div key={m._id || i} className="grid grid-cols-1 sm:grid-cols-[1fr_150px_auto_auto] gap-2 items-center p-2 rounded-lg border border-gray-200 dark:border-dark-100">
          <input value={m.title} onChange={(e) => update(i, { title: e.target.value })} placeholder="Milestone title" className={fieldClass} maxLength={200} aria-label="Milestone title" />
          <input type="date" value={m.date} onChange={(e) => update(i, { date: e.target.value })} className={fieldClass} aria-label="Milestone date" />
          <label className="inline-flex items-center gap-1.5 text-sm text-gray-700 dark:text-light-300">
            <input type="checkbox" checked={Boolean(m.done)} onChange={(e) => update(i, { done: e.target.checked })} /> Done
          </label>
          <button type="button" onClick={() => onChange(value.filter((_, idx) => idx !== i))} className="p-2 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 justify-self-end" aria-label="Remove milestone">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
    </div>
  );
};

export const SprintFormModal = ({ isOpen, onClose, sprint, onSaved }) => {
  const isEdit = Boolean(sprint);
  const { form, set, errors, serverError, saving, submit } = useCatalogForm({
    isOpen,
    initial: {
      name: sprint?.name || "",
      goal: sprint?.goal || "",
      description: sprint?.description || "",
      notes: sprint?.notes || "",
      startDate: toDateInput(sprint?.startDate),
      endDate: toDateInput(sprint?.endDate),
      milestones: (sprint?.milestones || []).map((m) => ({ ...m, date: toDateInput(m.date) })),
    },
    validate: (f) => {
      const er = {};
      if (!f.name.trim()) er.name = "Name is required";
      if (!f.startDate) er.startDate = "Start date is required";
      if (!f.endDate) er.endDate = "End date is required";
      if (f.startDate && f.endDate && f.endDate < f.startDate) er.endDate = "End date cannot be earlier than start date";
      if (f.milestones.some((m) => !m.title.trim())) er.milestones = "Every milestone needs a title";
      return er;
    },
    save: (f) => {
      const body = {
        ...f,
        name: f.name.trim(),
        milestones: f.milestones.map(({ _id, title, date, description, done }) => ({
          ...(_id ? { _id } : {}),
          title: title.trim(),
          date: date || null,
          description: description || "",
          done: Boolean(done),
        })),
      };
      return isEdit ? sprintService.update(sprint._id, body) : sprintService.create(body);
    },
    onSaved,
  });

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      title={isEdit ? "Edit sprint" : "New sprint"}
      subtitle="Sprints are workspace-wide: tasks from any project or squad can join"
      footer={<Footer onClose={onClose} onSubmit={submit} saving={saving} label={isEdit ? "Save" : "Create sprint"} />}
    >
      <form onSubmit={submit} className="space-y-4" noValidate>
        <ErrorBanner message={serverError} />
        <Input label="Name" required value={form.name} onChange={set("name")} error={errors.name} maxLength={100} autoFocus />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input label="Start date" type="date" required value={form.startDate} onChange={set("startDate")} error={errors.startDate} />
          <Input label="End date" type="date" required value={form.endDate} onChange={set("endDate")} error={errors.endDate} min={form.startDate || undefined} />
        </div>
        <p className="text-xs text-gray-500 dark:text-light-500">
          The status is automatic: Planned before the start date, Active from the start to the end date, Completed after it.
        </p>
        <TextArea label="Sprint goal" value={form.goal} onChange={set("goal")} rows={2} maxLength={1000} />
        <TextArea label="Description" value={form.description} onChange={set("description")} rows={4} maxLength={10000} />
        <TextArea label="Important notes" value={form.notes} onChange={set("notes")} rows={3} maxLength={10000} />
        <MilestonesEditor value={form.milestones} onChange={set("milestones")} />
        {errors.milestones && <span className="text-sm text-red-500">{errors.milestones}</span>}
        <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
      </form>
    </BaseModal>
  );
};
